/**
 * Check if a subscription provides active access (for viewing)
 * Returns true if subscription is active/trialing, or canceled/past_due but still within billing period
 * This allows users to VIEW their Norths even if subscription is canceled (until period ends)
 */
export function hasActiveSubscription(
  status: string,
  currentPeriodEnd: string | null,
  trialEndsAt?: string | null
): boolean {
  // Active subscriptions always have access
  if (status === 'active') {
    return true;
  }

  // Trialing subscriptions have access unless trial has expired
  // (guards against delayed/lost subscription.trial_ended webhooks)
  if (status === 'trialing') {
    if (trialEndsAt && new Date(trialEndsAt) < new Date()) return false;
    return true;
  }

  // Canceled, canceling, or past_due subscriptions only have access until period end
  // 'canceling' means subscription is scheduled to cancel but still active until period end
  if (status === 'canceled' || status === 'canceling' || status === 'past_due') {
    if (!currentPeriodEnd) {
      return false; // No period end means no access
    }

    const periodEnd = new Date(currentPeriodEnd);
    const now = new Date();

    // Still have access if period hasn't ended yet
    return periodEnd > now;
  }

  // Any other status (like 'unpaid', 'incomplete', etc.) = no access
  return false;
}

/**
 * Check if a subscription allows creating or editing Norths
 * Returns true ONLY for active/trialing subscriptions
 * Canceled subscriptions can VIEW but cannot CREATE/EDIT/UPDATE
 */
export function canCreateOrEditNorth(
  status: string,
  currentPeriodEnd: string | null,
  trialEndsAt?: string | null
): boolean {
  // Active subscriptions can always create/edit
  if (status === 'active') {
    return true;
  }

  // Trialing subscriptions can create/edit unless trial has expired
  if (status === 'trialing') {
    if (trialEndsAt && new Date(trialEndsAt) < new Date()) return false;
    return true;
  }

  // Canceled, canceling, or past_due subscriptions cannot create/edit
  // Even if they're still within the billing period, they can only view
  if (status === 'canceled' || status === 'canceling' || status === 'past_due') {
    return false;
  }

  // Any other status (like 'unpaid', 'incomplete', etc.) = no access
  return false;
}

/**
 * Check if a subscription tier can create Buyer Reports
 * Returns false for starter tier — Buyer Reports are a Lite+ feature
 */
export function canCreateBuyerReport(planTier: string | null | undefined): boolean {
  if (!planTier) return false;
  const tier = planTier.toLowerCase();
  return tier === 'lite' || tier === 'growth' || tier === 'scale';
}

/**
 * Check if a subscription tier can access the Offer Strategy AI feature
 * Available to Lite+ plans
 */
export function canAccessOfferStrategy(planTier: string | null | undefined): boolean {
  if (!planTier) return false;
  const tier = planTier.toLowerCase();
  return tier === 'lite' || tier === 'growth' || tier === 'scale';
}

/**
 * Get a user-friendly message explaining why access is restricted
 */
export function getAccessRestrictionMessage(
  status: string,
  currentPeriodEnd: string | null
): { title: string; description: string; action?: string } {
  // Handle both 'canceled' and 'canceling' statuses the same way
  if (status === 'canceled' || status === 'canceling') {
    const periodEnd = currentPeriodEnd ? new Date(currentPeriodEnd) : null;
    if (periodEnd && periodEnd > new Date()) {
      return {
        title: 'Subscription Canceled',
        description: `Your subscription has been canceled. You'll have access until ${periodEnd.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.`,
        action: 'Resume Subscription',
      };
    }
    return {
      title: 'Subscription Canceled',
      description: 'Your subscription has ended. Please resubscribe to continue using North Reports.',
      action: 'Resubscribe',
    };
  }

  if (status === 'past_due') {
    const periodEnd = currentPeriodEnd ? new Date(currentPeriodEnd) : null;
    if (periodEnd && periodEnd > new Date()) {
      return {
        title: 'Payment Failed',
        description: `Your payment could not be processed. Please update your payment method to continue. You have access until ${periodEnd.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.`,
        action: 'Update Payment Method',
      };
    }
    return {
      title: 'Payment Failed',
      description: 'Your payment could not be processed and your subscription has ended. Please update your payment method and resubscribe.',
      action: 'Update Payment Method',
    };
  }

  return {
    title: 'Access Restricted',
    description: 'Your subscription is not active. Please contact support for assistance.',
  };
}

